import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Button, message, Pagination, Table } from "antd";
import {
  EditFilled,
  EyeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import {
  buildColumnsWithHeaderFilters,
  HeaderTable,
  PermissionDirect,
  TableActionButtons,
} from "../../Components";
import { PlusIcon } from "../../assets/icon";
import { CommonApi, CustomerApi } from "../../services";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import { decorateEditableColumns } from "../../shared/helpers/editableColumns";
import {
  normalizeTouchedKey,
  setDeepImmutable,
  toPath,
} from "../../shared/helpers/objectPath";
import { buildCustomerColumns } from "./columns";
import { CustomerDetailModal } from "./CustomerDetailModal";
import {
  buildCustomerPayload,
  createCustomerDraft,
  getMissingCustomerRequiredFields,
  insertCustomerChildDraft,
  removeCustomerTreeNode,
  updateCustomerTreeNode,
} from "./helpers";
import styles from "./Customer.module.scss";

const createDraftId = () =>
  `create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getExpandableKeys = (rows: any[]) =>
  rows.filter((row) => row.children?.length).map((row) => String(row._id));

export const Customer = () => {
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [editRows, setEditRows] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
  const [detailCustomer, setDetailCustomer] = useState<any>(null);
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
  const saveLocksRef = useRef(new Set<string>());
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleFiltersChange = useCallback((nextFilters: any) => {
    unstable_batchedUpdates(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setFilters((prev: any) =>
        typeof nextFilters === "function"
          ? nextFilters(prev)
          : nextFilters,
      );
    });
  }, []);

  const handleUpdateCustomer = useCallback(
    (dataIndex: string | string[], value: any, rowId: string) => {
      setEditRows((prev) =>
        prev.map((item) => {
          if (String(item.index) !== String(rowId)) return item;
          return {
            ...item,
            record: setDeepImmutable(item.record, toPath(dataIndex), value),
            touchedFields: {
              ...(item.touchedFields ?? {}),
              [normalizeTouchedKey(dataIndex)]: true,
            },
          };
        }),
      );
    },
    [],
  );

  const handleAddCustomer = useCallback(
    (parent?: any) => {
      const draft = createCustomerDraft(createDraftId(), parent?._id);
      setDataSource((prev) =>
        parent
          ? insertCustomerChildDraft(prev, parent._id, draft)
          : [draft, ...prev],
      );
      setEditRows((prev) => [
        ...prev,
        { index: draft._id, record: draft, touchedFields: {} },
      ]);
      if (parent?._id) {
        setExpandedRowKeys((prev) =>
          Array.from(new Set([...prev, String(parent._id)])),
        );
      }
    },
    [],
  );

  const handleEditCustomer = useCallback((record: any) => {
    setEditRows((prev) =>
      prev.some((item) => String(item.index) === String(record._id))
        ? prev
        : [...prev, { index: record._id, record, touchedFields: {} }],
    );
  }, []);

  const handleCancelCustomer = useCallback(
    (rowId: string) => {
      if (saveLocksRef.current.has(String(rowId))) return;

      const editing = editRows.find(
        (item) => String(item.index) === String(rowId),
      );
      if (editing?.record?.status === "new") {
        setDataSource((rows) => removeCustomerTreeNode(rows, rowId));
      }
      setEditRows((prev) =>
        prev.filter((item) => String(item.index) !== String(rowId)),
      );
    },
    [editRows],
  );

  const handleSaveCustomer = useCallback(
    async (rowId: string) => {
      const lockKey = String(rowId);
      if (saveLocksRef.current.has(lockKey)) return;

      const editing = editRows.find(
        (item) => String(item.index) === lockKey,
      );
      if (!editing) return;

      const missingFields = getMissingCustomerRequiredFields(editing.record);
      if (missingFields.length) {
        messageApi.error("Vui lòng nhập đầy đủ các trường bắt buộc");
        return;
      }

      saveLocksRef.current.add(lockKey);
      setSavingRows((prev) => ({ ...prev, [lockKey]: true }));

      try {
        const payload = buildCustomerPayload(editing.record);
        const response =
          editing.record.status === "new"
            ? await CustomerApi.postCreateCustomer(payload)
            : await CustomerApi.putUpdateCustomer(payload, lockKey);

        if (!response?.success) {
          throw new Error(response?.message || "Lưu khách hàng thất bại");
        }

        const serverCustomer = response.data ?? response.customer ?? {};
        const persistedId = serverCustomer._id ?? response._id ?? lockKey;
        const persistedCustomer = {
          ...editing.record,
          ...serverCustomer,
          _id: persistedId,
        };
        delete persistedCustomer.status;

        setDataSource((prev) =>
          updateCustomerTreeNode(prev, lockKey, persistedCustomer),
        );
        setEditRows((prev) =>
          prev.filter((item) => String(item.index) !== lockKey),
        );
        if (editing.record.status === "new" && !editing.record.parent) {
          setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
        }
        messageApi.success("Lưu khách hàng thành công");
      } catch (error: any) {
        messageApi.error(error?.message || "Lưu khách hàng thất bại");
      } finally {
        saveLocksRef.current.delete(lockKey);
        setSavingRows((prev) => {
          const next = { ...prev };
          delete next[lockKey];
          return next;
        });
      }
    },
    [editRows, messageApi],
  );

  const columns = useMemo(() => {
    const editableColumns = decorateEditableColumns(
      buildCustomerColumns(provinceOptions) as any[],
      {
        editIndex: editRows,
        getDataSource: () => dataSource,
        onUpdate: handleUpdateCustomer,
      },
    );

    return buildColumnsWithHeaderFilters(
      [
        ...editableColumns,
        {
          title: "",
          key: "action",
          width: 120,
          fixed: "right",
          render: (_value: any, record: any) => {
            const editing = editRows.some(
              (item) => String(item.index) === String(record._id),
            );
            const saving = !!savingRows[String(record._id)];

            if (editing) {
              return (
                <div>
                  <Button
                    loading={saving}
                    disabled={saving}
                    onClick={() => handleSaveCustomer(record._id)}
                  >
                    Lưu
                  </Button>{" "}
                  <Button
                    disabled={saving}
                    onClick={() => handleCancelCustomer(record._id)}
                  >
                    Hủy
                  </Button>
                </div>
              );
            }

            return (
              <TableActionButtons
                items={[
                  {
                    key: "detail",
                    label: "Chi tiết",
                    icon: <EyeOutlined />,
                    tone: "info",
                    onClick: () => setDetailCustomer(record),
                  },
                  {
                    key: "create-child",
                    label: "Thêm khách hàng con",
                    icon: <PlusCircleOutlined />,
                    tone: "info",
                    hidden: Boolean(record.parent),
                    disabled: !checkPermissions("CUSTOMER", "add", features),
                    onClick: () => handleAddCustomer(record),
                  },
                  {
                    key: "edit",
                    label: "Chỉnh sửa",
                    icon: <EditFilled />,
                    tone: "edit",
                    disabled: !checkPermissions("CUSTOMER", "edit", features),
                    onClick: () => handleEditCustomer(record),
                  },
                ]}
              />
            );
          },
        },
      ],
      filters,
      handleFiltersChange,
    );
  }, [
    dataSource,
    editRows,
    features,
    filters,
    handleAddCustomer,
    handleCancelCustomer,
    handleEditCustomer,
    handleFiltersChange,
    handleSaveCustomer,
    handleUpdateCustomer,
    provinceOptions,
    savingRows,
  ]);

  useEffect(() => {
    CommonApi.getSearchProvine({ searchText: "" })
      .then((response: any) => {
        setProvinceOptions(
          (response?.data ?? []).map((province: any) => ({
            label: province.name,
            value: province.name,
          })),
        );
      })
      .catch(() => setProvinceOptions([]));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);

    CustomerApi.getAllCustomer({
      current: pagination.current,
      pageSize: pagination.pageSize,
      ...filters,
    })
      .then((response: any) => {
        if (!active) return;
        const rows = response?.data ?? [];
        unstable_batchedUpdates(() => {
          setDataSource(rows);
          setEditRows([]);
          setExpandedRowKeys(getExpandableKeys(rows));
          setPagination((prev) => ({
            ...prev,
            total: response?.totalItem ?? 0,
          }));
        });
      })
      .catch((error: any) => {
        if (active) {
          messageApi.error(error?.message || "Không thể tải danh sách khách hàng");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    filters,
    messageApi,
    pagination.current,
    pagination.pageSize,
  ]);

  return (
    <PermissionDirect screen={["CUSTOMER"]}>
      <div className={styles.Customer}>
        {contextHolder}
        <div className="container">
          <HeaderTable title="Danh sách khách hàng" />
          <div className={styles.TableWrapper}>
            <Table
              bordered
              size="small"
              loading={loading}
              rowKey={(record: any) => String(record._id)}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowKeys,
                expandIcon: () => null,
              }}
            />
          </div>
          <Button
            className={styles.ButtonPlus}
            icon={<PlusIcon />}
            onClick={() => handleAddCustomer()}
            disabled={!checkPermissions("CUSTOMER", "add", features)}
          >
            Thêm mới
          </Button>
          <Pagination
            className={styles.Pagination}
            align="end"
            showSizeChanger
            total={pagination.total}
            current={pagination.current}
            pageSize={pagination.pageSize}
            onChange={(current, pageSize) =>
              setPagination((prev) => ({
                ...prev,
                current,
                pageSize,
              }))
            }
          />
        </div>
        <CustomerDetailModal
          open={!!detailCustomer}
          customer={detailCustomer}
          onClose={() => setDetailCustomer(null)}
        />
      </div>
    </PermissionDirect>
  );
};
